import { useState } from "react"

import { type DateRange } from "react-day-picker"
import { ArrowRight, CalendarBlank, X } from "@phosphor-icons/react"
import { format, addMonths, subMonths } from "date-fns"

import {
  calendarDefaultMinDate,
  calendarDefaultMaxDate,
  convertToEndOfWeekUTC,
  convertToStartOfWeekUTC,
  formatDate as dtFormatDate,
  DateFormatType,
  setISOWeekDay,
  toUTCDateString,
} from "~/utils/datetime"
import { Calendar } from "~/components/reusable/Calendar"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/reusable/Popover"
import { Button } from "~/components/reusable/Button"
import { inputVariants } from "~/components/reusable/Input"
import { cn } from "~/utils/helpers"
import {
  type QuickSelectFilter,
  DEFAULT_QUICK_SELECTS,
} from "~/constants/dateRangeQucikSelectOptions"
import { Separator } from "~/components/reusable/Separator"
import { Input } from "./Input"
import { Alert, AlertDescription } from "~/components/reusable/Alert"
import { PeriodGrid, MONTH_ITEMS } from "~/components/reusable/DatePicker"

function toUTC(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

function isMonthBefore(a: Date, b: Date): boolean {
  return a.getFullYear() * 12 + a.getMonth() < b.getFullYear() * 12 + b.getMonth()
}

function monthIndex(d: Date): number {
  return d.getFullYear() * 12 + d.getMonth()
}

// Prevents react-day-picker from highlighting outside (greyed-out) days on a calendar
// the range doesn't reach — e.g. a Feb range bleeding into March's outside days.
function rangeForCalendar(range: DateRange | undefined, month: Date): DateRange | undefined {
  if (!range?.from) return range
  const cal = monthIndex(month)
  const from = monthIndex(range.from)
  const to = monthIndex(range.to ?? range.from)
  return to < cal || from > cal ? undefined : range
}

function SelectedDateRange({
  startDate,
  endDate,
  fmt,
}: {
  startDate: Date | undefined
  endDate: Date | undefined
  fmt: (d: Date) => string
}) {
  return (
    <div className="flex flex-row items-center gap-2 w-fit">
      <Input
        value={startDate ? fmt(startDate) : ""}
        disabled
        className="w-28"
        containerClassName="w-fit"
      />
      <ArrowRight />
      <Input
        value={endDate ? fmt(endDate) : ""}
        disabled
        className="w-28"
        containerClassName="w-fit"
      />
    </div>
  )
}

/**
 * Date range picker — opens a popover with dual calendars. For single date use DatePicker.
 * Canonical intent: {@link ../design-system/intents.ts DATE_RANGE_PICKER_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export default function DateRangePicker({
  onStartDateChange,
  onEndDateChange,
  onApply,
  trigger,
  initialPresetLabel,
  startDate,
  endDate,
  className,
  disabled,
  minDate = calendarDefaultMinDate,
  maxDate = calendarDefaultMaxDate,
  showDefaultQuickSelects = false,
  quickSelectOptions,
  customQuickSelects,
  align = "end",
  onlyMondays = false,
  onlyFirstOfMonth = false,
  clearable,
  validateRange,
  formatDate,
  invalid = false,
  weekly = false,
  size = "default",
}: {
  onStartDateChange?: (startDate: Date | undefined) => void
  onEndDateChange?: (endDate: Date | undefined) => void
  /**
   * Fired on Apply with the committed range plus which quick-select preset produced it
   * (null when the user picked dates manually). Callers that need the preset identity
   * (e.g. to store "Last 4 weeks" as a live period) use this instead of the two
   * per-date callbacks.
   */
  onApply?: (range: {
    from: Date | undefined
    to: Date | undefined
    presetLabel: string | null
  }) => void
  /** Custom trigger rendered in place of the built-in field button (slotted; suppresses the clear X). */
  trigger?: React.ReactNode
  /**
   * Pre-highlights this quick-select preset when the popover opens (e.g. editing a preset-backed
   * period); null opens with no highlight. When the prop is omitted entirely, the highlight keeps
   * its legacy behavior: the last applied preset stays highlighted across opens.
   */
  initialPresetLabel?: string | null
  startDate: Date | undefined
  endDate: Date | undefined
  className?: string
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  showDefaultQuickSelects?: boolean
  quickSelectOptions?: QuickSelectFilter[]
  customQuickSelects?: QuickSelectFilter[]
  align?: "start" | "center" | "end"
  onlyMondays?: boolean
  onlyFirstOfMonth?: boolean
  clearable?: boolean
  validateRange?: (start: Date | undefined, end: Date | undefined) => string | null
  formatDate?: (date: Date) => string
  /**
   * Whole-week selection: any clicked day selects its entire ISO week. The range is
   * normalized to Monday (start) → Sunday (end) and the label shows the ISO week. Use
   * when the consumer stores/applies values per week (e.g. forecast adjustments).
   */
  weekly?: boolean
  invalid?: boolean
  /** "small" (h-7) matches size="sm" controls, e.g. in ViewConfiguration toolbars. */
  size?: "default" | "small"
}) {
  const [open, setOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string | undefined>()
  const [leftMonth, setLeftMonth] = useState<Date>(startDate ?? new Date())
  const [rightMonth, setRightMonth] = useState<Date>(
    endDate && isMonthBefore(startDate ?? new Date(), endDate)
      ? endDate
      : addMonths(startDate ?? new Date(), 1),
  )
  const [leftYear, setLeftYear] = useState<number>((startDate ?? new Date()).getFullYear())
  const [rightYear, setRightYear] = useState<number>((endDate ?? new Date()).getFullYear())
  const [localRange, setLocalRange] = useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  })

  const [clampNotes, setClampNotes] = useState<string[]>([])
  // True when the applied range no longer equals what the highlighted preset resolves to (min/max
  // clamping, or the toUTC + weekly re-snap shifting it in west-of-UTC timezones). `onApply` then
  // reports presetLabel null so callers store the dates the user saw, not a preset that re-resolves
  // to something else.
  const [presetAdjusted, setPresetAdjusted] = useState(false)
  // weekly mode: tracks whether the next click finishes the range (true) or starts a new
  // single-week anchor (false). Mirrors the from/!to "pending" state of the day picker,
  // but each anchor highlights the whole week so we can't rely on `to` being undefined.
  const [weeklyPickingEnd, setWeeklyPickingEnd] = useState(false)

  const weekStartOf = (d: Date) => toUTC(setISOWeekDay(d, 1))
  const weekEndOf = (d: Date) => toUTC(setISOWeekDay(d, 7))

  // If a selected range reaches a month neither calendar is showing — e.g. a week that
  // spills across a month boundary (Mon 31 Aug → Sun 6 Sep) — snap the view so the user
  // sees the full range instead of silently losing the part in the hidden month.
  const revealRange = (from: Date | undefined, to: Date | undefined) => {
    if (!from) return
    const end = to ?? from
    const isVisible = (d: Date) =>
      monthIndex(d) === monthIndex(leftMonth) || monthIndex(d) === monthIndex(rightMonth)
    if (isVisible(from) && isVisible(end)) return
    setLeftMonth(from)
    setRightMonth(isMonthBefore(from, end) ? end : addMonths(from, 1))
  }

  const builtInOptions = quickSelectOptions ?? DEFAULT_QUICK_SELECTS
  const hasQuickSelects = builtInOptions.length > 0 || (customQuickSelects?.length ?? 0) > 0
  const validationMessage = validateRange?.(localRange?.from, localRange?.to)
  const alertMessages = [...clampNotes, ...(validationMessage ? [validationMessage] : [])]

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setWeeklyPickingEnd(false)
      setPresetAdjusted(false)
      // Only callers that own the preset identity re-seed the highlight; for everyone else the
      // last-applied preset stays highlighted across opens (pre-existing behavior).
      if (initialPresetLabel !== undefined) setSelectedKey(initialPresetLabel ?? undefined)
      setLocalRange(
        weekly
          ? {
              from: startDate ? weekStartOf(startDate) : undefined,
              to: endDate ? weekEndOf(endDate) : undefined,
            }
          : { from: startDate, to: endDate },
      )
      const left = startDate ?? new Date()
      const right = endDate ?? addMonths(new Date(), 1)
      setLeftMonth(left)
      setRightMonth(isMonthBefore(left, right) ? right : addMonths(left, 1))
      setLeftYear((startDate ?? new Date()).getFullYear())
      setRightYear((endDate ?? addMonths(new Date(), 1)).getFullYear())
      setClampNotes([])
    }
    setOpen(nextOpen)
  }

  // full range → reset to new start | partial → complete (order normalized) | none → set start
  const handleRangeSelect = (date: Date) => {
    // A manual calendar click supersedes any highlighted preset, so `selectedKey` (and the
    // presetLabel reported at Apply) must not keep claiming the range came from the preset.
    setSelectedKey(undefined)
    if (weekly) {
      // First click anchors a single full week; second click extends to the clicked
      // week (order normalized); a third click starts over.
      if (weeklyPickingEnd && localRange?.from) {
        const anchor = localRange.from
        const next =
          date.getTime() < anchor.getTime()
            ? { from: weekStartOf(date), to: weekEndOf(anchor) }
            : { from: anchor, to: weekEndOf(date) }
        setLocalRange(next)
        revealRange(next.from, next.to)
        setWeeklyPickingEnd(false)
      } else {
        const next = { from: weekStartOf(date), to: weekEndOf(date) }
        setLocalRange(next)
        revealRange(next.from, next.to)
        setWeeklyPickingEnd(true)
      }
      return
    }
    if (localRange?.from && localRange?.to) {
      setLocalRange({ from: date, to: undefined })
    } else if (localRange?.from) {
      const { from } = localRange
      setLocalRange(date.getTime() < from.getTime() ? { from: date, to: from } : { from, to: date })
    } else {
      setLocalRange({ from: date, to: undefined })
    }
  }

  const handleApply = () => {
    onStartDateChange?.(localRange?.from)
    onEndDateChange?.(localRange?.to)
    onApply?.({
      from: localRange?.from,
      to: localRange?.to,
      presetLabel: !presetAdjusted ? (selectedKey ?? null) : null,
    })
    setOpen(false)
  }
  const handleQuickSelect = (filter: QuickSelectFilter) => {
    setSelectedKey(filter.label)
    const newRange = filter.resolve(minDate, maxDate)
    if (!newRange) return
    let from = newRange.from ? toUTC(newRange.from) : undefined
    let to = newRange.to ? toUTC(newRange.to) : undefined
    const notes: string[] = []
    if (from && from < minDate) {
      from = minDate
      notes.push(
        `Data only available from ${dtFormatDate(minDate, DateFormatType.FullDateShort)}. Period adjusted`,
      )
    }
    if (to && to > maxDate) {
      to = maxDate
      notes.push(
        `Data only available until ${dtFormatDate(maxDate, DateFormatType.FullDateShort)}. Period adjusted`,
      )
    }
    if (weekly) {
      from = from ? weekStartOf(from) : undefined
      to = to ? weekEndOf(to) : undefined
    }
    // Compare at UTC-day granularity against the preset's UTC-week-snapped range: time-of-day
    // normalization and weekly snapping are what a weekly consumer re-applies when it re-resolves
    // the preset, so neither is an adjustment. Only a genuinely different DAY (min/max clamping,
    // or the toUTC local-timezone shift for west-of-UTC users) means the preset would re-resolve
    // to a different period than the one applied.
    const sameUTCDay = (a: Date | undefined, b: Date | undefined) =>
      a && b ? toUTCDateString(a) === toUTCDateString(b) : a === b
    const expectedFrom =
      newRange.from && weekly ? convertToStartOfWeekUTC(newRange.from) : newRange.from
    const expectedTo = newRange.to && weekly ? convertToEndOfWeekUTC(newRange.to) : newRange.to
    setPresetAdjusted(!sameUTCDay(from, expectedFrom) || !sameUTCDay(to, expectedTo))
    setClampNotes(notes)
    setLocalRange({ from, to })
    if (from) {
      setLeftMonth(from)
      setRightMonth(to && isMonthBefore(from, to) ? to : addMonths(from, 1))
      setLeftYear(from.getFullYear())
      setRightYear(to ? to.getFullYear() : from.getFullYear() + 1)
    }
  }

  const sharedCalendarProps = {
    mode: "range" as const,
    weekStartsOn: 1 as const,
    captionLayout: "dropdown" as const,
    className: "w-fit",
    disabled: [
      ...(minDate ? [{ before: minDate }] : []),
      ...(maxDate ? [{ after: maxDate }] : []),
      ...(onlyMondays ? [(date: Date) => date.getDay() !== 1] : []),
      ...(onlyFirstOfMonth ? [(date: Date) => date.getDate() !== 1] : []),
    ],
    numberOfMonths: 1,
    showOutsideDays: true,
    components: {
      Nav: ({ className: navCn, children, ...props }: React.ComponentProps<"div">) => (
        <div className={cn(navCn)} {...props}>
          {children}
        </div>
      ),
    },
  }

  const showClear = clearable && (!!startDate || !!endDate)
  const fmt =
    formatDate ??
    (weekly
      ? (d: Date) =>
          `${dtFormatDate(d, DateFormatType.WeekShort)} · ${dtFormatDate(d, DateFormatType.DayMonth)}`
      : (d: Date) => format(d, "dd/MM/yyyy"))

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {trigger ? (
        <PopoverTrigger asChild disabled={disabled}>
          {trigger}
        </PopoverTrigger>
      ) : (
        <div className="relative w-full">
          <PopoverTrigger asChild disabled={disabled}>
            <button
              type="button"
              data-empty={!startDate || !endDate}
              aria-invalid={invalid}
              className={cn(
                inputVariants({ size }),
                "flex cursor-pointer items-center justify-start gap-2 text-left font-normal",
                "data-[empty=true]:text-fg-muted!",
                "data-[state=open]:shadow-borders-interactive-with-active",
                invalid && "focus:shadow-borders-error shadow-borders-error",
                showClear && "pr-8",
                className,
              )}
            >
              <CalendarBlank />
              {startDate ? (
                endDate ? (
                  <>
                    {fmt(startDate)} - {fmt(endDate)}
                  </>
                ) : (
                  <>{fmt(startDate)} - DD/MM/YYYY</>
                )
              ) : (
                <span>DD/MM/YYYY - DD/MM/YYYY</span>
              )}
            </button>
          </PopoverTrigger>
          {showClear && (
            <X
              className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-fg-muted hover:text-fg"
              onClick={() => {
                onStartDateChange?.(undefined)
                onEndDateChange?.(undefined)
              }}
            />
          )}
        </div>
      )}

      <PopoverContent className="w-auto flex flex-row p-0" align={align}>
        {hasQuickSelects && showDefaultQuickSelects && (
          <div className="p-3 min-w-[220px] bg-subtle rounded-l-[12px] border-r border-border-base">
            <ul className="flex flex-col">
              {builtInOptions.map((filter) => {
                const isSelected = filter.label === selectedKey
                const isDisabled = !filter.resolve(minDate, maxDate)
                return (
                  <li
                    key={filter.label}
                    onClick={() => !isDisabled && handleQuickSelect(filter)}
                    className={cn(
                      "px-2 py-1.5 cursor-pointer rounded-md label-base text-fg-subtle",
                      !isDisabled && "hover:bg-base-hover",
                      isSelected && "bg-base-hover font-medium",
                      isDisabled && "pointer-events-none text-fg-muted",
                    )}
                  >
                    {filter.label}
                  </li>
                )
              })}
              {customQuickSelects && customQuickSelects.length > 0 && (
                <>
                  {builtInOptions.length > 0 && (
                    <Separator orientation="horizontal" className="my-1.5" />
                  )}
                  {customQuickSelects.map((filter) => {
                    const isSelected = filter.label === selectedKey
                    const isDisabled = !filter.resolve(minDate, maxDate)
                    return (
                      <li
                        key={filter.label}
                        onClick={() => !isDisabled && handleQuickSelect(filter)}
                        className={cn(
                          "px-2 py-1.5 cursor-pointer rounded-md label-base text-fg-subtle",
                          !isDisabled && "hover:bg-base-hover",
                          isSelected && "bg-base-hover font-medium",
                          isDisabled && "pointer-events-none text-fg-muted",
                        )}
                      >
                        {filter.label}
                      </li>
                    )
                  })}
                </>
              )}
            </ul>
          </div>
        )}
        <div className="flex flex-col">
          <div className="flex flex-row gap-4 p-3">
            {onlyFirstOfMonth ? (
              <>
                <PeriodGrid
                  items={MONTH_ITEMS}
                  cols={3}
                  year={leftYear}
                  onYearChange={(y) => {
                    setLeftYear(y)
                    if (y >= rightYear) setRightYear(y + 1)
                  }}
                  selectedRange={localRange}
                  onSelect={handleRangeSelect}
                  minDate={minDate}
                  maxDate={maxDate}
                />
                <Separator orientation="vertical" className="h-auto -my-3" />
                <PeriodGrid
                  items={MONTH_ITEMS}
                  cols={3}
                  year={rightYear}
                  onYearChange={(y) => {
                    setRightYear(y)
                    if (y <= leftYear) setLeftYear(y - 1)
                  }}
                  selectedRange={localRange}
                  onSelect={handleRangeSelect}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </>
            ) : (
              <>
                <Calendar
                  {...sharedCalendarProps}
                  selected={rangeForCalendar(localRange, leftMonth)}
                  month={leftMonth}
                  onSelect={(_range: DateRange | undefined, day: Date) =>
                    handleRangeSelect(toUTC(day))
                  }
                  onMonthChange={(month) => {
                    setLeftMonth(month)
                    if (!isMonthBefore(month, rightMonth)) setRightMonth(addMonths(month, 1))
                  }}
                  startMonth={minDate}
                  endMonth={maxDate}
                />
                <Separator orientation="vertical" className="h-82 -my-3" />
                <Calendar
                  {...sharedCalendarProps}
                  selected={rangeForCalendar(localRange, rightMonth)}
                  month={rightMonth}
                  onSelect={(_range: DateRange | undefined, day: Date) =>
                    handleRangeSelect(toUTC(day))
                  }
                  onMonthChange={(month) => {
                    setRightMonth(month)
                    if (!isMonthBefore(leftMonth, month)) setLeftMonth(subMonths(month, 1))
                  }}
                  startMonth={minDate}
                  endMonth={maxDate}
                />
              </>
            )}
          </div>
          <Separator orientation="horizontal" />
          {alertMessages.length > 0 && (
            <div className="w-0 min-w-full px-3 pt-3">
              <Alert variant={validationMessage ? "warning" : "info"}>
                <AlertDescription>
                  {alertMessages.map((msg, i) => (
                    <span key={i} className={i > 0 ? "block mt-1" : "block"}>
                      {msg}
                    </span>
                  ))}
                </AlertDescription>
              </Alert>
            </div>
          )}
          <div
            className={cn(
              "flex flex-row justify-between p-3 bg-subtle rounded-br-[12px]",
              !(hasQuickSelects && showDefaultQuickSelects) && "rounded-bl-[12px]",
            )}
          >
            <SelectedDateRange startDate={localRange?.from} endDate={localRange?.to} fmt={fmt} />
            <div className="flex flex-row gap-2">
              <Button type="button" variant="neutral" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleApply}
                disabled={!!validationMessage}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
